import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { contractApi } from '../../../services/contractApi';
import { propertyApi } from '../../../services/propertyApi';
import { profileApi } from '../../../services/profileApi';
import { roomPayApi } from '../../../services/roomPayApi';
import { ProfileType } from '../../../utils/profileType';
import {
  buildPaymentHistoryItems,
  getContractsRoleForProfile,
  getMonthlyRentAmount,
  getPropertyAddress,
  mapRoomPayHistoryRecords,
  resolveRentalHistoryEndDate,
  resolveRentalHistoryStartDate,
  resolveRentalStatus,
  type PaymentHistoryItem,
} from '../../../utils/rentalPaymentHistory';
import { RENTAL_HISTORY_REFRESH_EVENT } from '../../../utils/dashboardRefresh';
import { isNativeMobileApp } from '../../../utils/mobileApp';
import { resolveTenantContractInfo } from '../../../utils/contractTenantInfo';

interface RentalHistoryItem {
  id: string;
  title: string;
  propertyName: string;
  roomNumber: string | null;
  startDate: string;
  endDate: string;
  amount: number;
  counterparty: string;
  tenantName: string;
  tenantPhone: string | null;
  tenantCccdLast4: string | null;
  status: 'active' | 'expired' | 'pending' | 'completed' | 'cancelled' | 'draft';
}

const cccdLast4 = (value?: string | null): string | null => {
  const digits = (value || '').replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : null;
};

const normalizeText = (value?: string | null): string => {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const stripRoomSearchLabels = (value: string): string => {
  return normalizeText(value)
    .replace(/\b(so\s+)?(can|phong|room|unit|canho|can\s+ho)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const matchesHistorySearch = (
  query: string,
  parts: Array<string | null | undefined>,
): boolean => {
  const normalized = normalizeText(query);
  if (!normalized) return true;

  const haystack = normalizeText(parts.filter(Boolean).join(' '));
  if (haystack.includes(normalized)) return true;

  const strippedQuery = stripRoomSearchLabels(query);
  const strippedHaystack = stripRoomSearchLabels(parts.filter(Boolean).join(' '));
  if (strippedQuery && (strippedHaystack.includes(strippedQuery) || haystack.includes(strippedQuery))) {
    return true;
  }

  return false;
};

const getPropertyDisplayName = (property: any, contract?: any, fallback?: string): string => {
  const baseName = property?.title || property?.name || fallback || 'Thuê nhà';
  const roomNumber = property?.room_number || contract?.room_number;
  if (roomNumber?.trim()) {
    return `${baseName} - Số phòng ${roomNumber.trim()}`;
  }
  return baseName;
};

const getRoomNumber = (property: any, contract?: any): string | null => {
  const unitNumber = [
    property?.room_number,
    property?.unit_number,
    property?.apartment_number,
    property?.house_number,
    contract?.room_number,
    contract?.unit_number,
  ].find((value) => typeof value === 'string' && value.trim());

  return typeof unitNumber === 'string' && unitNumber.trim() ? unitNumber.trim() : null;
};

interface RentalHistoryProps {
  profileType?: ProfileType;
}

const RentalHistory: React.FC<RentalHistoryProps> = ({ profileType = 'tenant' }) => {
  const isNative = isNativeMobileApp();
  const showTenantContact = profileType === 'landlord' || profileType === 'business';
  const { user } = useAuth();
  useTranslation();
  const [historyItems, setHistoryItems] = useState<RentalHistoryItem[]>([]);
  const [paymentHistoryItems, setPaymentHistoryItems] = useState<PaymentHistoryItem[]>([]);
  const [activeSubTab, setActiveSubTab] = useState<'rent' | 'payment'>('rent');
  const [filterText, setFilterText] = useState('');
  const [filterMonth, setFilterMonth] = useState<string>('');
  const [filterYear, setFilterYear] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRentalHistory = useCallback(async () => {
    if (!user?.id) {
      setHistoryItems([]);
      setPaymentHistoryItems([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const backendContracts = await contractApi.getContracts({
        role: getContractsRoleForProfile(profileType),
        account_type: profileType,
      });

      const propertyIds = [...new Set(backendContracts.map((c: any) => c.property_id))];
      const propertyResults = await Promise.all(
        propertyIds.map((id) => propertyApi.getPropertyById(id.toString()).catch(() => null))
      );

      const propertyMap = new Map<number, any>();
      propertyResults.forEach((result, index) => {
        if (result?.data) {
          propertyMap.set(propertyIds[index], result.data);
        }
      });

      const counterpartyIds = new Set<number>();
      backendContracts.forEach((c: any) => {
        if (profileType === 'tenant' && c.landlord_id) {
          counterpartyIds.add(c.landlord_id);
        } else if (profileType === 'landlord' && c.tenant_id) {
          counterpartyIds.add(c.tenant_id);
        }
      });

      const userResults = await Promise.all(
        Array.from(counterpartyIds).map((id) => profileApi.getUserById(id).catch(() => null))
      );

      const userMap = new Map<number, any>();
      Array.from(counterpartyIds).forEach((id, index) => {
        if (userResults[index]) {
          userMap.set(id, userResults[index]);
        }
      });

      const mapped: RentalHistoryItem[] = backendContracts.map((contract: any) => {
        const property = propertyMap.get(contract.property_id);
        const propertyName = getPropertyDisplayName(property, contract, `Property #${contract.property_id}`);
        const address = getPropertyAddress(property, contract);
        const roomNumber = getRoomNumber(property, contract);
        const monthlyRent = getMonthlyRentAmount(contract, property);

        let counterparty = 'Unknown';
        let counterpartyId: number | undefined;

        if (profileType === 'tenant') {
          counterpartyId = contract.landlord_id;
        } else if (profileType === 'landlord' || profileType === 'business') {
          if (contract.tenant_name) {
            counterparty = contract.tenant_name;
          } else {
            counterpartyId = contract.tenant_id;
          }
        }

        if (counterpartyId && counterparty === 'Unknown') {
          const counterpartyUser = userMap.get(counterpartyId);
          counterparty = counterpartyUser?.full_name || counterpartyUser?.name || counterpartyUser?.username || `User #${counterpartyId}`;
        }

        const status = resolveRentalStatus(contract, property, profileType) as RentalHistoryItem['status'];

        const tenantUser = contract.tenant_id ? userMap.get(contract.tenant_id) : undefined;
        let tenantName = (contract.tenant_name || '').trim();
        if (!tenantName && tenantUser) {
          tenantName = tenantUser.full_name || tenantUser.name || tenantUser.username || '';
        }
        const tenantInfo = resolveTenantContractInfo(contract);
        const tenantPhone = (tenantInfo.phone || tenantUser?.phone || '').trim() || null;
        const tenantCccdLast4 = cccdLast4(tenantInfo.cccd || tenantUser?.cccd);

        return {
          id: contract.id.toString(),
          title: address,
          propertyName,
          roomNumber,
          startDate: resolveRentalHistoryStartDate(contract),
          endDate: resolveRentalHistoryEndDate(contract),
          amount: monthlyRent,
          counterparty,
          tenantName,
          tenantPhone,
          tenantCccdLast4,
          status,
        };
      });

      const resolveRoomNumberForProperty = (propertyId?: number | null) => {
        if (propertyId == null) return null;
        const property = propertyMap.get(Number(propertyId));
        const contract = backendContracts.find(
          (c: any) => Number(c.property_id) === Number(propertyId),
        );
        return getRoomNumber(property, contract);
      };

      let paymentHistorySeed: PaymentHistoryItem[];
      if (profileType === 'landlord' || profileType === 'business') {
        const records = await roomPayApi.getPaymentHistory();
        const recordsById = new Map(records.map((record) => [String(record.id), record]));
        paymentHistorySeed = mapRoomPayHistoryRecords(records).map((item) => {
          const record = recordsById.get(String(item.id));
          return {
            ...item,
            roomNumber:
              item.roomNumber
              || resolveRoomNumberForProperty(record?.property_id)
              || record?.pay_room_code
              || null,
            tenantName: item.tenantName || record?.tenant_name || null,
          };
        });
      } else {
        paymentHistorySeed = buildPaymentHistoryItems(
          backendContracts,
          propertyMap,
          profileType,
          (contract, property) => getPropertyAddress(property, contract),
        ).map((item) => {
          const contract = backendContracts.find(
            (c: any) => item.id === `payment-${c.id}` || item.id === `deposit-${c.id}`,
          );
          return {
            ...item,
            roomNumber: item.roomNumber || getRoomNumber(
              contract ? propertyMap.get(contract.property_id) : undefined,
              contract,
            ),
          };
        });
      }

      setHistoryItems(mapped);
      setPaymentHistoryItems(paymentHistorySeed);
    } catch (err: any) {
      console.error('Error loading rental history:', err);
      setError(err?.message || 'Không thể tải lịch sử thuê');
      setHistoryItems([]);
      setPaymentHistoryItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [profileType, user?.id]);

  useEffect(() => {
    loadRentalHistory();
  }, [loadRentalHistory]);

  useEffect(() => {
    const handleRefresh = () => {
      loadRentalHistory();
    };

    window.addEventListener(RENTAL_HISTORY_REFRESH_EVENT, handleRefresh);
    return () => window.removeEventListener(RENTAL_HISTORY_REFRESH_EVENT, handleRefresh);
  }, [loadRentalHistory]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadRentalHistory();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadRentalHistory]);

  const getStatusBadge = (status: RentalHistoryItem['status']) => {
    switch (status) {
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-blue-100 text-blue-700';
      case 'pending':
        return 'bg-green-100 text-green-700';
      case 'expired':
        return 'bg-red-100 text-red-700';
      case 'cancelled':
        return 'bg-gray-200 text-gray-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusLabel = (status: RentalHistoryItem['status']) => {
    switch (status) {
      case 'active':
        return 'Đang thuê';
      case 'completed':
        return 'Hoàn thành';
      case 'pending':
        return 'Đang chờ thuê';
      case 'expired':
        return 'Hết hạn';
      case 'cancelled':
        return 'Đã hủy';
      default:
        return 'Bản nháp';
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return '—';
    const datePart = value.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    if (!year || !month || !day) return value;

    return new Date(year, month - 1, day).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatCurrency = (value?: number) => {
    if (!value) return '—';
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);
  };

  const getPaymentStatusBadge = (status: PaymentHistoryItem['status']) => {
    if (status === 'paid') return 'bg-green-100 text-green-700';
    if (status === 'overdue') return 'bg-red-100 text-red-700';
    return 'bg-amber-100 text-amber-700';
  };

  const getPaymentStatusLabel = (status: PaymentHistoryItem['status']) => {
    if (status === 'paid') return 'Đã thanh toán';
    if (status === 'overdue') return 'Quá hạn thanh toán';
    return 'Chờ thanh toán';
  };

  const visibleHistoryItems = useMemo(() => {
    const normalizedFilter = normalizeText(filterText);
    const month = filterMonth ? parseInt(filterMonth, 10) : null;
    const year = filterYear ? parseInt(filterYear, 10) : null;

    return historyItems.filter((item) => {
      const searchableParts = [
        item.title,
        item.propertyName,
        item.roomNumber,
        item.tenantName,
        item.counterparty,
        showTenantContact ? item.tenantPhone : null,
        showTenantContact ? item.tenantCccdLast4 : null,
      ];
      if (normalizedFilter && !matchesHistorySearch(filterText, searchableParts)) return false;

      // If month/year filter applied, check whether the rental period overlaps the selected month
      if (month && year) {
        const start = item.startDate || '';
        const end = item.endDate || '';
        const inMonth = (dateStr: string) => {
          const datePart = dateStr.split('T')[0];
          const [y, m] = datePart.split('-').map(Number);
          return y === year && m === month;
        };

        const periodOverlaps = () => {
          if (inMonth(start) || inMonth(end)) return true;
          // If start <= selectedMonthEnd and (no end or end >= selectedMonthStart)
          try {
            const selStart = new Date(year, month - 1, 1);
            const selEnd = new Date(year, month, 0, 23, 59, 59);
            const s = start ? new Date(start.split('T')[0]) : null;
            const e = end ? new Date(end.split('T')[0]) : null;
            if (s && e) return s <= selEnd && e >= selStart;
            if (s && !e) return s <= selEnd; // ongoing
            return false;
          } catch (err) {
            return false;
          }
        };

        if (!periodOverlaps()) return false;
      }

      return true;
    });
  }, [filterMonth, filterText, filterYear, historyItems, showTenantContact]);

  const visiblePaymentHistoryItems = useMemo(() => {
    const normalizedFilter = normalizeText(filterText);
    const month = filterMonth ? parseInt(filterMonth, 10) : null;
    const year = filterYear ? parseInt(filterYear, 10) : null;

    return paymentHistoryItems.filter((item) => {
      const searchableParts = [
        item.title,
        item.description,
        item.paymentTerm,
        item.roomNumber,
        item.tenantName,
      ];
      if (normalizedFilter && !matchesHistorySearch(filterText, searchableParts)) return false;

      if (month && year) {
        const paid = item.paidDate || item.billingPeriodStart || '';
        if (!paid) return false;
        const datePart = paid.split('T')[0];
        const [y, m] = datePart.split('-').map(Number);
        if (y !== year || m !== month) return false;
      }

      return true;
    });
  }, [filterMonth, filterText, filterYear, paymentHistoryItems]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3">
        <div className="rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3">
            <div className={isNative ? 'grid grid-cols-2 gap-2' : 'flex flex-wrap gap-2'}>
              <button
              type="button"
              onClick={() => setActiveSubTab('rent')}
              className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${isNative ? 'text-center' : ''} ${activeSubTab === 'rent' ? 'bg-[#C4A35A] text-white' : 'text-black/70 hover:text-black'}`}
            >
              <i className={`fas fa-file-contract ${isNative ? 'mr-1.5' : 'mr-2'}`}></i>
              Lịch sử thuê
            </button>
            <button
              type="button"
              onClick={() => setActiveSubTab('payment')}
              className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${isNative ? 'text-center' : ''} ${activeSubTab === 'payment' ? 'bg-[#C4A35A] text-white' : 'text-black/70 hover:text-black'}`}
            >
              <i className={`fas fa-credit-card ${isNative ? 'mr-1.5' : 'mr-2'}`}></i>
              Lịch sử thanh toán
            </button>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="flex-1 rounded-full border border-black/10 bg-[#f8f5ee] px-3 py-2 flex items-center gap-2">
                <i className="fas fa-search text-black/40 text-sm"></i>
                <input
                  type="text"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder="Tìm theo địa chỉ, số phòng, tên hoặc SĐT khách thuê"
                  className="w-full bg-transparent text-sm text-black outline-none placeholder:text-black/40"
                />
              </div>

              <div className="flex items-center gap-2">
                <select
                  value={filterMonth}
                  onChange={(e) => setFilterMonth(e.target.value)}
                  className="rounded-full border border-black/10 bg-white px-3 py-2 text-sm"
                  aria-label="Chọn tháng lọc"
                >
                  <option value="">Tháng</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={`${m}`}>
                      {`Tháng ${m}`}
                    </option>
                  ))}
                </select>

                <select
                  value={filterYear}
                  onChange={(e) => setFilterYear(e.target.value)}
                  className="rounded-full border border-black/10 bg-white px-3 py-2 text-sm"
                  aria-label="Chọn năm lọc"
                >
                  <option value="">Năm</option>
                  {(() => {
                    const cy = new Date().getFullYear();
                    return Array.from({ length: 6 }, (_, i) => cy - i).map((y) => (
                      <option key={y} value={`${y}`}>
                        {y}
                      </option>
                    ));
                  })()}
                </select>

                {(filterMonth || filterYear) && (
                  <button
                    type="button"
                    onClick={() => {
                      setFilterMonth('');
                      setFilterYear('');
                    }}
                    className="rounded-full px-3 py-2 text-sm font-medium text-[#C4A35A] hover:bg-[#C4A35A]/10"
                  >
                    Xóa thời gian
                  </button>
                )}

                {filterText && (
                  <button
                    type="button"
                    onClick={() => setFilterText('')}
                    className="rounded-full px-3 py-2 text-sm font-medium text-[#C4A35A] hover:bg-[#C4A35A]/10"
                  >
                    Xóa
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-black/10 bg-white p-6 text-center text-sm text-black/60">
          <i className="fas fa-spinner fa-spin mb-2 text-[#C4A35A]"></i>
          <p>Đang tải lịch sử thuê...</p>
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
          {error}
        </div>
      ) : (
        <div className="space-y-6">
          {activeSubTab === 'rent' ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <i className="fas fa-file-contract text-[#C4A35A]"></i>
                <h4 className="font-semibold text-black">Lịch sử thuê</h4>
              </div>
              {visibleHistoryItems.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-black/15 bg-white p-8 text-center text-sm text-black/60">
                  <p>{filterText ? 'Không tìm thấy kết quả phù hợp với từ khóa này.' : 'Chưa có lịch sử thuê nào để hiển thị.'}</p>
                </div>
              ) : (
                visibleHistoryItems.map((item) => (
                  <div key={item.id} className="rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="font-semibold text-black">{item.title}</h4>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${getStatusBadge(item.status)}`}>
                            {getStatusLabel(item.status)}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-3 text-sm text-black/60">
                          <span>Số phòng: {item.roomNumber || '—'}</span>
                          <span>•</span>
                          <span>Đối tác: {item.counterparty}</span>
                          {showTenantContact && (
                            <>
                              <span>•</span>
                              <span>SĐT: {item.tenantPhone || '—'}</span>
                              <span>•</span>
                              <span>CCCD: {item.tenantCccdLast4 ? `***${item.tenantCccdLast4}` : '—'}</span>
                            </>
                          )}
                          <span>•</span>
                          <span>Bắt đầu: {formatDate(item.startDate)}</span>
                          <span>•</span>
                          <span>Kết thúc: {formatDate(item.endDate)}</span>
                        </div>
                      </div>
                      <div className="text-left md:text-right">
                        <p className="text-lg font-semibold text-[#C4A35A]">{formatCurrency(item.amount)}</p>
                        <p className="text-xs text-black/50">/ tháng</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <i className="fas fa-credit-card text-[#C4A35A]"></i>
                <h4 className="font-semibold text-black">Lịch sử thanh toán</h4>
              </div>
              {visiblePaymentHistoryItems.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-black/15 bg-white p-8 text-center text-sm text-black/60">
                  <p>{filterText ? 'Không tìm thấy kết quả phù hợp với từ khóa này.' : 'Chưa có lịch sử thanh toán nào để hiển thị.'}</p>
                </div>
              ) : (
                visiblePaymentHistoryItems.map((item) => (
                  <div key={item.id} className="rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="font-semibold text-black">{item.title}</h4>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${getPaymentStatusBadge(item.status)}`}>
                            {getPaymentStatusLabel(item.status)}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-sm text-black/60">
                          <span>Số phòng: {item.roomNumber || '—'}</span>
                          <span>•</span>
                          <span>Ngày thanh toán: {formatDate(item.paidDate)}</span>
                          {item.paymentTerm && (
                            <>
                              <span>•</span>
                              <span>Kỳ hạn thanh toán: {item.paymentTerm}</span>
                            </>
                          )}
                          {item.description && (
                            <>
                              <span>•</span>
                              <span>{item.description}</span>
                            </>
                          )}
                          {item.nextPaymentDate && (
                            <>
                              <span>•</span>
                              <span>Thanh toán tiếp theo: {formatDate(item.nextPaymentDate)}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="text-left md:text-right">
                        <p className="text-lg font-semibold text-[#C4A35A]">{formatCurrency(item.amount)}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RentalHistory;
